import { getEvaluationRules } from '@/lib/posture-analysis/evaluation-rules-load';

const evaluationRules = () => getEvaluationRules() as any;

// ---------------------------------------------------------------------------
// YAML-driven threshold getters (single source of truth for intake card framing)
//
// These correspond directly to evaluation-rules.yaml `sop.thresholds`:
//   intakeLimitationMinShortfallDeg  — red frame when nominal - measured > this
//   intakeHypermobileMinSurplusDeg   — yellow frame when measured - nominal > this
//
// Both use STRICT greater-than (>), not >=, per the SOP screenshot spec.
// ---------------------------------------------------------------------------

/**
 * YAML `sop.thresholds.intakeLimitationMinShortfallDeg`
 * Red frame fires when: nominal − measured > this value (strict).
 */
export function getSopIntakeLimitationMinShortfallDeg(): number {
  const raw = evaluationRules()?.sop?.thresholds?.intakeLimitationMinShortfallDeg;
  const n = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(n) && n >= 0 ? n : 10;
}

/**
 * YAML `sop.thresholds.intakeHypermobileMinSurplusDeg`
 * Yellow (hypermobile) frame fires when: measured − nominal > this value (strict).
 */
export function getSopIntakeHypermobileMinSurplusDeg(): number {
  const raw = evaluationRules()?.sop?.thresholds?.intakeHypermobileMinSurplusDeg;
  const n = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(n) && n >= 0 ? n : 20;
}

// ---------------------------------------------------------------------------
// Legacy compat — kept for any callers outside the intake card path that still
// read a single "band" value. Falls back to the limitation threshold so
// behavior stays consistent with the dominant use-case (limitation coloring).
// ---------------------------------------------------------------------------

/** @deprecated Use getSopIntakeLimitationMinShortfallDeg() or getSopIntakeHypermobileMinSurplusDeg() */
export function getSopLimitationGreenBandDeg(): number {
  return getSopIntakeLimitationMinShortfallDeg();
}

export type SopIntakeGoalBandStatus = 'none' | 'near-goal' | 'below-goal' | 'hypermobile';

/**
 * Unified per-side framing for bilateral SOP passive inputs (degrees).
 *
 * Rules (per evaluation-rules.yaml and SOP screenshot spec):
 *   - limitation (red):   nominal − measured > intakeLimitationMinShortfallDeg (default 10°)
 *   - hypermobile (yellow): measured − nominal > intakeHypermobileMinSurplusDeg (default 20°)
 *   - pass (green / near-goal): otherwise (both thresholds not exceeded)
 *
 * Note: BOTH use strict greater-than (>). At exactly the threshold value → green.
 *
 * This is the canonical implementation — sop-evaluation.ts getSopIntakeGoalFrameState()
 * delegates here so the API response and UI cards use identical logic.
 */
export function classifyIntakeGoalFrameState(
  measured: number | null,
  nominal: number,
): Exclude<SopIntakeGoalBandStatus, 'none'> {
  if (measured === null || !Number.isFinite(measured) || !Number.isFinite(nominal)) {
    return 'near-goal';
  }
  const limitationThreshold = getSopIntakeLimitationMinShortfallDeg();
  const hypermobileThreshold = getSopIntakeHypermobileMinSurplusDeg();

  const shortfall = nominal - measured;
  if (shortfall > limitationThreshold) return 'below-goal';

  const surplus = measured - nominal;
  if (surplus > hypermobileThreshold) return 'hypermobile';

  return 'near-goal';
}

/**
 * @deprecated Use classifyIntakeGoalFrameState() instead.
 * Kept for backward compatibility with TestInputCard which uses this function name.
 */
export function classifyMeasurementVersusGoalDeg(
  measurement: number,
  nominalDeg: number,
): Exclude<SopIntakeGoalBandStatus, 'none'> {
  return classifyIntakeGoalFrameState(measurement, nominalDeg);
}
